import { useCallback } from "react";
import type { ApiErrorKind, ValidationErrors } from "@shared/contracts";
import { toast } from "@shared/components/atoms/toast";
import { ApiError } from "@shared/api";
import { useUIStore } from "@app/store";
import { isNavigableError } from "./isNavigableError";
import type { HandleErrorOptions, HandleSuccessOptions } from "./Props";

interface ErrorDescription {
  message: string;
  status?: number;
  code?: ApiErrorKind;
  messages?: string[];
  /** A 422's `{ field: [messages] }` map, for binding to form inputs. */
  fieldErrors?: ValidationErrors;
}

/**
 * Hook for handling API responses (errors and successes) with toast notifications
 *
 * Features:
 * - Reads the backend's typed error contract off the thrown `ApiError`
 * - Allows overriding with custom messages
 * - Optionally sets errors in UI store for navigation handling
 *
 * @example
 * ```typescript
 * const { handleError, handleSuccess } = useHandleResponse();
 *
 * handleError({ error: apiError, context: "Loading Users" });
 *
 * handleError({
 *   error: apiError,
 *   context: "Loading Users",
 *   customMessage: "Failed to load",
 *   navigateOnError: true
 * });
 *
 * handleSuccess({
 *   data: response,
 *   context: "Create Item",
 *   customMessage: "Item created successfully!"
 * });
 * ```
 */
export function useHandleResponse() {
  const { setError } = useUIStore();

  /**
   * Every failure the API client raises is an `ApiError` carrying the
   * backend's envelope, so there is exactly one shape to read — no probing of
   * axios-style `error.response.data.*` nesting.
   */
  const describeError = useCallback((error: unknown): ErrorDescription => {
    if (ApiError.isApiError(error)) {
      return {
        message: error.message,
        status: error.statusCode,
        code: error.kind,
        messages: [...error.messages],
        fieldErrors: error.fieldErrors,
      };
    }

    if (error instanceof Error) {
      return { message: error.message };
    }

    return { message: "An error occurred" };
  }, []);

  /**
   * Mutations resolve to the whole response envelope, so this reads the
   * backend's already-localized `message`. Pass `customMessage` only to
   * override it.
   */
  const extractSuccessMessage = useCallback((data: unknown): string => {
    if (typeof data === "object" && data !== null && "message" in data) {
      const { message } = data as { message?: unknown };
      if (typeof message === "string") return message;
    }
    return "Operation completed successfully";
  }, []);

  const handleError = useCallback(
    ({
      error,
      context,
      customMessage,
      navigateOnError = true,
      showToast = true,
    }: HandleErrorOptions) => {
      const described = describeError(error);
      const message = customMessage || described.message;
      const title = context ? `${context} Error` : "Error";

      console.error(`[${title}]`, error);

      if (showToast) {
        toast({
          variant: "destructive",
          title,
          description: message,
        });
      }

      // A caller opting in still cannot navigate on a failure that must leave
      // the current view standing — see `isNavigableError`.
      if (navigateOnError && isNavigableError(described.code)) {
        setError({
          message,
          status: described.status,
          code: described.code,
          messages: described.messages,
          navigate: true,
        });
      }
    },
    [describeError, setError]
  );

  const handleSuccess = useCallback(
    ({ data, context, customMessage }: HandleSuccessOptions) => {
      const message = customMessage || extractSuccessMessage(data);
      const title = context ? `${context} Success` : "Success";

      toast({
        title,
        description: message,
      });
    },
    [extractSuccessMessage]
  );

  return {
    handleError,
    handleSuccess,
    describeError,
    extractSuccessMessage,
  };
}
